import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import type {
    DiscountCondition} from "@@/shared/discounts/conditions/discount-condition";
import {
    discountConditionZodSchema,
} from "@@/shared/discounts/conditions/discount-condition";
import type { DiscountId} from "@@/shared/discounts/discount-id";
import { discountIdFactory } from "@@/shared/discounts/discount-id";
import type { EntityState} from "@@/shared/entity-status";
import { entityStateZodSchema } from "@@/shared/entity-status";
import type {
    MetaContent} from "@@/shared/meta-content/meta-content";
import {
    metaContentZodSchema,
} from "@@/shared/meta-content/meta-content";
import { ownerIdZodSchema, type OwnerId } from "@@/shared/owner-id";
import { percentageZodSchema, type Percentage } from "@@/shared/percentage";
import type { UserId} from "@@/shared/users/user-id";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { Except } from "type-fest";
import { z } from "zod";

type PercentageDiscountType = "PERCENTAGE_DISCOUNT";

/**
 * If conditions are met, the value of the discount is subtracted from the the final price.
 */
type PercentageDiscount = {
    _id: DiscountId;
    _createdAt: IsoAndUnixTimestamp;
    _createdBy: UserId;
    _status: EntityState;
    meta: MetaContent;
    ownerId: OwnerId;
    type: PercentageDiscountType;
    conditions: DiscountCondition[];
    value: Percentage;
};

type FactoryParams = Except<PercentageDiscount, "_id" | "_createdAt" | "type">;
const createPercentageDiscount = (
    params: FactoryParams & { _id?: DiscountId },
): PercentageDiscount => {
    return {
        ...params,
        _id: params._id ?? discountIdFactory(),
        _createdAt: nowIsoAndUnix(),
        type: "PERCENTAGE_DISCOUNT",
    };
};

const percentageDiscountZodObject = z.object({
    _id: z.string() as unknown as z.ZodSchema<DiscountId>,
    _createdAt: isoAndUnixZodSchema,
    _createdBy: userIdZodSchema,
    _status: entityStateZodSchema,
    meta: metaContentZodSchema,
    ownerId: ownerIdZodSchema,
    type: z.literal("PERCENTAGE_DISCOUNT"),
    conditions: z.array(discountConditionZodSchema),
    value: percentageZodSchema,
});
const percentageDiscountZodSchema: z.ZodSchema<PercentageDiscount> =
    percentageDiscountZodObject;

export {
    createPercentageDiscount,
    percentageDiscountZodObject,
    percentageDiscountZodSchema,
};
export type { PercentageDiscount, PercentageDiscountType };
